<script setup lang="ts">

import {OpenaiCharacter} from "@/api/dx-backend";
import GmCharSelectorCard from "@/components/GameMaster/Character/GMCharSelectorCard.vue";
import {ref, computed, watch} from "vue";

interface Filter {
  name?: string;
  id?: string;
  positionId?: string;
  orgId?: string;
  npc?: boolean;
}

interface Props {
  characters: OpenaiCharacter[];
  isDraggable?: boolean;
  filtered?: Filter;
  selectedCharacterId?: string;
}

const props = withDefaults(defineProps<Props>(), {
  isDraggable: false,
  filtered: () => ({} as Filter),
  selectedCharacterId: undefined,
});
const emit = defineEmits<{
  select: [characterId: string];
  filterByPosition: [positionId: string];
  filterByOrganisation: [orgId: string];
  close: [];
}>();

// Filter state
const searchQuery = ref('');
const selectedOrgId = ref('');
const selectedNpcFilter = ref('');
const selectedPositionId = ref('');

// Get unique organizations from characters
const organizations = computed(() => {
  const orgs = new Set<string>();
  props.characters.forEach(char => {
    if (char.organization?.id) {
      orgs.add(char.organization.id);
    }
  });
  return Array.from(orgs).map(id => {
    const char = props.characters.find(c => c.organization?.id === id);
    return {
      id,
      name: char?.organization?.name || `Organization ${id.slice(0, 8)}`
    };
  });
});

// Filtered characters based on search and filters
const filteredCharacters = computed(() => {
  let filtered = props.characters;

  // Apply search query (name, id, or position)
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase().trim();
    filtered = filtered.filter(char =>
      char.name.toLowerCase().includes(query) ||
      char.id.toLowerCase().includes(query) ||
      char.position_id?.toLowerCase().includes(query) ||
      char.rank?.name?.toLowerCase().includes(query)
    );
  }

  // Apply organization filter
  if (selectedOrgId.value) {
    filtered = filtered.filter(char => char.organization?.id === selectedOrgId.value);
  }

  // Apply position filter
  if (selectedPositionId.value) {
    filtered = filtered.filter(char => char.position_id === selectedPositionId.value);
  }

  // Apply NPC filter
  if (selectedNpcFilter.value !== '') {
    const isNpc = selectedNpcFilter.value === 'true';
    filtered = filtered.filter(char => char.npc === isNpc);
  }

  return filtered;
});

// Fast action handlers
const copyCharacterId = (characterId: string) => {
  navigator.clipboard.writeText(characterId);
};

const filterByCharacterPosition = (character: OpenaiCharacter) => {
  if (character.position_id) {
    selectedPositionId.value = character.position_id;
    emit('filterByPosition', character.position_id);
  }
};

const filterByCharacterOrganisation = (character: OpenaiCharacter) => {
  if (character.organization?.id) {
    selectedOrgId.value = character.organization.id;
    emit('filterByOrganisation', character.organization.id);
  }
};

// Active filters computed property
const activeFilters = computed(() => {
  const filters = [];

  if (searchQuery.value.trim()) {
    filters.push({
      type: 'search',
      label: `Search: "${searchQuery.value}"`,
      value: searchQuery.value
    });
  }

  if (selectedOrgId.value) {
    const org = organizations.value.find(o => o.id === selectedOrgId.value);
    filters.push({
      type: 'organization',
      label: `Organization: ${org?.name || 'Unknown'}`,
      value: selectedOrgId.value
    });
  }

  if (selectedNpcFilter.value !== '') {
    const label = selectedNpcFilter.value === 'true' ? 'NPCs Only' : 'Players Only';
    filters.push({
      type: 'npc',
      label: label,
      value: selectedNpcFilter.value
    });
  }

  if (selectedPositionId.value) {
    filters.push({
      type: 'position',
      label: `Position: ${selectedPositionId.value.slice(0, 8)}...`,
      value: selectedPositionId.value
    });
  }

  return filters;
});

// Clear all filters function
const clearAllFilters = () => {
  searchQuery.value = '';
  selectedOrgId.value = '';
  selectedNpcFilter.value = '';
  selectedPositionId.value = '';
};

// Individual filter removal functions
const removeFilter = (filterType: string) => {
  switch (filterType) {
    case 'search':
      searchQuery.value = '';
      break;
    case 'organization':
      selectedOrgId.value = '';
      break;
    case 'npc':
      selectedNpcFilter.value = '';
      break;
    case 'position':
      selectedPositionId.value = '';
      break;
  }
};

// Auto-filter by position when a character is selected
watch(() => props.selectedCharacterId, (newSelectedId) => {
  if (newSelectedId) {
    const selectedCharacter = props.characters.find(char => char.id === newSelectedId);
    if (selectedCharacter?.position_id) {
      selectedPositionId.value = selectedCharacter.position_id;
    }
  }
}, { immediate: true });
</script>

<template>
  <div class="gm-panel gm-panel--raised char-selector-container">
    <div class="header">
      <div class="header-top">
        <h2 class="gm-title title">Character Selector</h2>
        <button @click="emit('close')" class="gm-btn gm-btn--ghost gm-btn--sm close-btn" title="Close Character Selector">
          ×
        </button>
      </div>

      <!-- Filter Controls -->
      <div class="filters">
        <!-- Search Input -->
        <div class="filter-group">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name, ID, position..."
            class="gm-input"
          />
        </div>

        <!-- Organization Filter -->
        <div class="filter-group">
          <select v-model="selectedOrgId" class="gm-select">
            <option value="">All Organizations</option>
            <option v-for="org in organizations" :key="org.id" :value="org.id">
              {{ org.name }}
            </option>
          </select>
        </div>

        <!-- NPC Filter -->
        <div class="filter-group">
          <select v-model="selectedNpcFilter" class="gm-select">
            <option value="">All Characters</option>
            <option value="true">NPCs Only</option>
            <option value="false">Players Only</option>
          </select>
        </div>

        <!-- Clear All Filters Button -->
        <div class="filter-group clear-button-group">
          <button
            @click="clearAllFilters"
            class="gm-btn gm-btn--sm gm-btn--danger clear-all-btn"
            :disabled="activeFilters.length === 0"
            title="Clear all active filters"
          >
            Clear All
          </button>
        </div>
      </div>

      <!-- Active Filter Tags -->
      <div v-if="activeFilters.length > 0" class="filter-tags">
        <div class="gm-label filter-tags-label">Active Filters:</div>
        <div class="filter-tags-list">
          <div
            v-for="filter in activeFilters"
            :key="filter.type"
            class="gm-chip filter-tag"
            @click="removeFilter(filter.type)"
          >
            <span class="filter-tag-label">{{ filter.label }}</span>
            <button
              class="filter-tag-remove"
              title="Remove this filter"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="characters-grid-wrapper gm-scroll">
      <div class="characters-grid">
        <GmCharSelectorCard
          v-for="character in filteredCharacters"
          :key="character.id"
          :character="character"
          :selected="character.id === props.selectedCharacterId"
          class="character-card"
          @click="emit('select', character.id)"
          @copy-id="copyCharacterId"
          @filter-by-position="filterByCharacterPosition"
          @filter-by-organisation="filterByCharacterOrganisation"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.char-selector-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 25rem;
  max-height: 37.5rem;
}

.header {
  flex-shrink: 0;
  margin-bottom: var(--gm-gap-sm);
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-sm);
  position: relative;
}

.title {
  flex: 1;
  text-align: center;
}

.close-btn {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  padding: 0;
}

.filters {
  display: flex;
  gap: var(--gm-gap-sm);
  flex-wrap: wrap;
  align-items: center;
}

.filter-group {
  flex: 1;
  min-width: 8.75rem;
}

.clear-button-group {
  flex: 0 0 auto;
  min-width: auto;
}

.filter-tags {
  margin-top: var(--gm-gap-sm);
  padding-top: var(--gm-gap-sm);
  border-top: 1px solid var(--gm-border-dim);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--gm-gap-sm);
}

.filter-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-xs);
}

.filter-tag {
  cursor: pointer;
}

.filter-tag-label {
  margin-right: 0.25rem;
}

.filter-tag-remove {
  background: none;
  border: none;
  color: inherit;
  font-size: 0.75rem;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

@media (max-width: 40rem) {
  .filters {
    flex-direction: column;
  }

  .filter-group {
    min-width: 100%;
  }

  .clear-button-group {
    min-width: 100%;
  }
}

.characters-grid-wrapper {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: var(--gm-gap-xs);
}

.characters-grid {
  display: grid;
  gap: var(--gm-gap-sm);
  padding: var(--gm-gap-xs);
  grid-template-columns: 1fr;
}

@media (min-width: 40rem) {
  .characters-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 48rem) {
  .characters-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (min-width: 64rem) {
  .characters-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

@media (min-width: 80rem) {
  .characters-grid {
    grid-template-columns: repeat(5, 1fr);
  }
}

@media (min-width: 96rem) {
  .characters-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

.character-card {
  cursor: pointer;
  border-radius: var(--gm-radius);
  overflow: hidden;
}
</style>
